// @flow

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import classNames from "classnames";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { Button, ButtonGroup, Pagination, Loader, GSGrid, View } from "@components";
import { AbsenceReasonsAddModal, AbsenceReasonsEditModal } from "@modules/service/components";
import { formatDate } from "@transformers/date";
import type { State } from "@store/reducers";
import type { AbsenceReason, FilterType } from "@modules/service/reducer/absence-reasons/types";
import type { ApprovableContainer, FilterState } from "@modules/service/types";
import { type PaginationExtendedType } from "@types";
import { ABSENCE_REASON_TYPES, ABSENCE_REASON_STATE } from "@modules/service/constants";

type PropsFromState = $Exact<{
  absenceReason: ?AbsenceReason,
  list: Array<ApprovableContainer<AbsenceReason>>,
  total: number,
  pagination: PaginationExtendedType,
  filterState: FilterState,
  filterType: FilterType,
  absenceReasonSchema: ?Object,
  isLoading: boolean,
}>;

export type AbsenceReasonContainer = {
  alertsCount: number,
  messagesCount: number,
  questionsCount: number,
  reportsCount: number,
  rtwFormsCount: number,
  ...ApprovableContainer<AbsenceReason>,
};

type PropsFromDispatch = $Exact<{
  updateAbsenceReason: typeof actionCreators.UpdateAbsenceReason,
  getAbsenceReasonContainer: typeof actionCreators.GetAbsenceReason,
  resetAbsenceReason: typeof actionCreators.ResetAbsenceReason,
  getAbsenceReasons: typeof actionCreators.GetAbsenceReasonsList,
  newAbsenceReason: typeof actionCreators.NewAbsenceReason,
  changeFilterState: typeof actionCreators.ChangeAbsenceReasonsFilterState,
  changeFilterType: typeof actionCreators.ChangeAbsenceReasonsFilterType,
  exportLiveAbsenceReasons: typeof actionCreators.ExportLiveAbsenceReasons,
  addNewAbsenceReason: typeof actionCreators.AddNewAbsenceReason,
  getAbsenceReasonSchema: typeof actionCreators.GetAbsenceReasonSchema,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  clientId: number,
  showADDModal: boolean,
  showEDITModal: boolean,
}>;

const ReasonsTypesMap = {
  ALL: "All", // null
  MEDICAL: "Medical", // 2
  NON_MEDICAL: "Non-Medical", //1
};

export function AbsenceReasons(props: Props) {
  const [showADDModal, setShowADDModal] = useState(false);
  const [showEDITModal, setShowEDITModal] = useState(false);

  const [absenceReasonsSearchParams, setAbsenceReasonsSearchParams] = useState({
    clientId: props.clientId,
    isActive: ABSENCE_REASON_STATE.ACTIVE,
    absenceReasonTypeId: ABSENCE_REASON_TYPES.ALL,
    page: 1,
  });

  useEffect(() => {
    props.getAbsenceReasonSchema();
  }, [props.clientId]);

  useEffect(() => {
    props.getAbsenceReasons(absenceReasonsSearchParams);
  }, [absenceReasonsSearchParams]);

  function handleChangeisActiveFilter(filter: null | boolean) {
    return function() {
      setAbsenceReasonsSearchParams({
        ...absenceReasonsSearchParams,
        isActive: filter,
        page: 1,
      });
    };
  }

  function handleChangeFilterType(filter: number | null) {
    return function() {
      setAbsenceReasonsSearchParams({
        ...absenceReasonsSearchParams,
        absenceReasonTypeId: filter,
        page: 1,
      });
    };
  }
  function handleAddAbsenceReason() {
    props.newAbsenceReason();
    setShowADDModal(true);
  }
  function handleEditAbsenceReason(row: Object) {
    props.getAbsenceReasonContainer({
      clientId: row.clientId,
      stableId: row.id,
    });
    setShowEDITModal(true);
  }

  function handleExport() {
    props.exportLiveAbsenceReasons({
      clientId: props.clientId,
    });
  }

  function addAbsenceReason(formData: Object) {
    const { isActive, absenceReasonTypeId } = absenceReasonsSearchParams;

    props.addNewAbsenceReason({
      clientId: props.clientId,
      draft: formData,
      isActive:
        isActive !== ABSENCE_REASON_STATE.ALL ? isActive === ABSENCE_REASON_STATE.ACTIVE : null,
      absenceReasonTypeId:
        absenceReasonTypeId !== ABSENCE_REASON_TYPES.ALL ? absenceReasonTypeId : null,
    });

    setShowADDModal(false);
  }

  function handleChangePage(page: number) {
    props.getAbsenceReasons({
      ...absenceReasonsSearchParams,
      page,
    });
  }

  function handleModalAddClose() {
    setShowADDModal(false);
    props.resetAbsenceReason();
  }

  function handleModalEditClose() {
    props.resetAbsenceReason();
    setShowEDITModal(false);
  }

  function updateAbsenceReason(formData: Object) {
    const { isActive, absenceReasonTypeId } = absenceReasonsSearchParams;

    props.updateAbsenceReason({
      clientId: props.clientId,
      stableId: props.absenceReason?.id,
      draft: formData,
      isActive:
        isActive !== ABSENCE_REASON_STATE.ALL ? isActive === ABSENCE_REASON_STATE.ACTIVE : null,
      absenceReasonTypeId:
        absenceReasonTypeId !== ABSENCE_REASON_TYPES.ALL ? absenceReasonTypeId : null,
    });

    setShowEDITModal(false);
  }

  type ValueAndScore = {
    value: any,
    isScoring: boolean,
  };

  type ValuesAndScoreRecord = {
    alerts: ValueAndScore,
    messages: ValueAndScore,
    questions: ValueAndScore,
    reports: ValueAndScore,
    RTWForms: ValueAndScore,
  };

  function getValuesAndScore(row: AbsenceReasonContainer): ValuesAndScoreRecord {
    const { alertsCount, messagesCount, questionsCount, reportsCount, rtwFormsCount } = row;
    return {
      alerts: { value: alertsCount, isScoring: alertsCount > 0 },
      messages: { value: messagesCount, isScoring: messagesCount > 0 },
      questions: { value: questionsCount, isScoring: questionsCount > 0 },
      reports: { value: reportsCount, isScoring: reportsCount > 0 },
      RTWForms: { value: rtwFormsCount, isScoring: rtwFormsCount > 0 },
    };
  }

  function getColorPillRender(value: number | string | boolean, isGreen: boolean) {
    return GSGrid.render.colorPill(value, isGreen && "mint", isGreen && "mint-d2");
  }

  const columns = [
    {
      name: props.t("ID"),
      key: "id",
    },
    {
      name: props.t("ABSENCE_REASONS_ABSENCE_REASON"),
      align: "left",
      renderCell: ({ row }: Object) => row.content.name,
    },
    {
      name: props.t("ABSENCE_REASONS_TYPE"),
      width: 105,
      align: "left",
      renderCell: ({ row }: Object) => row.content.parentReason.typeName,
    },
    {
      name: props.t("ABSENCE_REASONS_DATE_ADDED"),
      width: 100,
      align: "center",
      renderCell: ({ row }: Object) => GSGrid.render.date(row.createdAt),
    },

    {
      key: "alerts",
      name: props.t("ALERTS"),
      width: 100,
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ABSENCE_REASONS_ALERTS"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const alerts = getValuesAndScore(row).alerts;
        return getColorPillRender(alerts.value, alerts.isScoring);
      },
    },
    {
      key: "messages",
      name: props.t("MESSAGES"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ABSENCE_REASONS_MESSAGES"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const messages = getValuesAndScore(row).messages;
        return getColorPillRender(messages.value, messages.isScoring);
      },
    },
    {
      key: "questions",
      name: props.t("QUESTIONS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ABSENCE_REASONS_QUESTIONS"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const questions = getValuesAndScore(row).questions;
        return getColorPillRender(questions.value, questions.isScoring);
      },
    },
    {
      key: "reports",
      name: props.t("REPORTS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ABSENCE_REASONS_REPORTS"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const reports = getValuesAndScore(row).reports;
        return getColorPillRender(reports.value, reports.isScoring);
      },
    },
    {
      key: "rtw_forms",
      name: props.t("RTW_FORMS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ABSENCE_REASONS_RTW_FORMS"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const RTWForms = getValuesAndScore(row).RTWForms;
        return getColorPillRender(RTWForms.value, RTWForms.isScoring);
      },
    },
    {
      key: "rating",
      name: props.t("RATING"),
      renderCell: ({ row }: { row: AbsenceReasonContainer }) => {
        const valuesAndScore = Object.values(getValuesAndScore(row));

        if (!Array.isArray(valuesAndScore)) return;
        //$FlowIgnore
        const totalScore = valuesAndScore.filter(v => v.isScoring === true).length;
        return GSGrid.render.scorePill(totalScore);
      },
    },
    {
      name: props.t("STATUS"),
      width: 105,
      renderCell: ({ row }: Object) =>
        GSGrid.render.statusPill(props.t(row.content.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      name: props.t("EDIT"),
      width: 40,
      align: "center",
      onCellClick: handleEditAbsenceReason,
      renderCell: ({ row }: Object) => GSGrid.render.materialIcon("edit"),
    },
  ];

  return (
    <View>
      {props.isLoading && <Loader />}
      <View className="message-box" margin={[0, 0, "1rem", 0]}>
        {props.t("ABSENCE_REASONS_INFORMATION")}
      </View>
      <View className="gs-container" padding={["1rem"]}>
        <View justifyContent="space-between" direction="row" margin={[0, 0, "1rem", 0]}>
          <View direction="row">
            <ButtonGroup
              config={[
                {
                  label: props.t("ALL"),
                  color:
                    absenceReasonsSearchParams.isActive === ABSENCE_REASON_STATE.ALL && "selected",
                  onClick: handleChangeisActiveFilter(ABSENCE_REASON_STATE.ALL),
                },
                {
                  label: props.t("ACTIVE"),
                  color:
                    absenceReasonsSearchParams.isActive === ABSENCE_REASON_STATE.ACTIVE &&
                    "selected",
                  onClick: handleChangeisActiveFilter(ABSENCE_REASON_STATE.ACTIVE),
                },
                {
                  label: props.t("INACTIVE"),
                  color:
                    absenceReasonsSearchParams.isActive === ABSENCE_REASON_STATE.INACTIVE &&
                    "selected",
                  onClick: handleChangeisActiveFilter(ABSENCE_REASON_STATE.INACTIVE),
                },
              ]}
            ></ButtonGroup>

            <View margin={[0, 0, 0, "1rem"]}>
              <ButtonGroup
                config={[
                  {
                    label: props.t("ALL"),
                    color:
                      absenceReasonsSearchParams.absenceReasonTypeId === ABSENCE_REASON_TYPES.ALL &&
                      "selected",
                    onClick: handleChangeFilterType(ABSENCE_REASON_TYPES.ALL),
                  },
                  {
                    label: props.t("MEDICAL"),
                    color:
                      absenceReasonsSearchParams.absenceReasonTypeId ===
                        ABSENCE_REASON_TYPES.MEDICAL && "selected",
                    onClick: handleChangeFilterType(ABSENCE_REASON_TYPES.MEDICAL),
                  },
                  {
                    label: props.t("NON_MEDICAL"),
                    color:
                      absenceReasonsSearchParams.absenceReasonTypeId ===
                        ABSENCE_REASON_TYPES.NON_MEDICAL && "selected",
                    onClick: handleChangeFilterType(ABSENCE_REASON_TYPES.NON_MEDICAL),
                  },
                ]}
              ></ButtonGroup>
            </View>
          </View>
          <View direction="row">
            <Button materialIcon="file_download" onClick={handleExport}>
              {props.t("EXPORT")}
            </Button>
            <Button materialIcon="add_circle" className="ml-2" onClick={handleAddAbsenceReason}>
              {props.t("ADD")}
            </Button>
          </View>
        </View>
        {props.list.length ? (
          <GSGrid columns={columns} rows={props.list} maxHeight="100%" />
        ) : (
          <div className="my-5 d-flex justify-content-center">
            <h3 className="text-pinkish-grey">{props.t("ABSENCE_REASONS_LIVE_EMPTY")}</h3>
          </div>
        )}
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={props.pagination.pageNumber}
          totalPages={props.pagination.totalPages}
          onChange={handleChangePage}
        />
      </View>
      {showADDModal && (
        <AbsenceReasonsAddModal
          schema={props.absenceReasonSchema}
          onSubmit={addAbsenceReason}
          onClose={handleModalAddClose}
        />
      )}
      {showEDITModal && props.absenceReason && (
        <AbsenceReasonsEditModal
          schema={props.absenceReasonSchema}
          onSubmit={updateAbsenceReason}
          onClose={handleModalEditClose}
          data={props.absenceReason}
          providerId={props.absenceReason?.id}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    absenceReason: serviceSelectors.getAbsenceReasonContainer(state),
    list: serviceSelectors.getAbsenceReasonsList(state),
    total: serviceSelectors.getAbsenceReasonsTotalElements(state),
    pagination: serviceSelectors.getAbsenceReasonsPagination(state),
    filterState: serviceSelectors.getAbsenceReasonsFilterState(state),
    filterType: serviceSelectors.getAbsenceReasonsFilterType(state),
    absenceReasonSchema: serviceSelectors.absenceReasonSchema(state),
    isLoading: serviceSelectors.getAbsenceReasonsLoading(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  updateAbsenceReason: actionCreators.UpdateAbsenceReason,
  getAbsenceReasonContainer: actionCreators.GetAbsenceReason,
  resetAbsenceReason: actionCreators.ResetAbsenceReason,
  getAbsenceReasons: actionCreators.GetAbsenceReasonsList,
  newAbsenceReason: actionCreators.NewAbsenceReason,
  changeFilterState: actionCreators.ChangeAbsenceReasonsFilterState,
  changeFilterType: actionCreators.ChangeAbsenceReasonsFilterType,
  exportLiveAbsenceReasons: actionCreators.ExportLiveAbsenceReasons,
  addNewAbsenceReason: actionCreators.AddNewAbsenceReason,
  getAbsenceReasonSchema: actionCreators.GetAbsenceReasonSchema,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AbsenceReasons);
